import React, { useState, useEffect } from 'react'
import { Download, Filter } from 'lucide-react'
import { apiClient } from '@/services/api'
import { Button } from '@/components/ui/Button'
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate'

interface StockAdjustmentsReportProps {
  // No props needed as this component manages its own state
}

export const StockAdjustmentsReport: React.FC<StockAdjustmentsReportProps> = () => {
  const { template: currentTemplate } = useCurrentTemplate()
  const [adjustments, setAdjustments] = useState<any[]>([])
  const [filteredAdjustments, setFilteredAdjustments] = useState<any[]>([])
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [showAdjustmentFilters, setShowAdjustmentFilters] = useState(false)
  const [activeTab, setActiveTab] = useState<'active' | 'undone' | 'all'>('active')

  useEffect(() => {
    fetchStockAdjustments()
  }, [])

  useEffect(() => {
    applyAdjustmentFilters()
  }, [adjustments, startDate, endDate, activeTab])

  const fetchStockAdjustments = async () => {
    setAdjustmentsLoading(true);
    try {
      const response = await apiClient.get('/materials/stock-adjustments');
      const adjustmentsData = response?.data || response || [];
      setAdjustments(adjustmentsData);
    } catch (error) {
      console.error('Error fetching stock adjustments:', error);
      setAdjustments([]);
    } finally {
      setAdjustmentsLoading(false);
    }
  };

  // Helper function to identify undone adjustments by parsing undo operation notes
  const getUndoneAdjustmentIds = (): Set<string> => {
    const undoneIds = new Set<string>();
    
    adjustments.forEach(adj => {
      if (adj.reason && adj.reason.toLowerCase().includes('undo adjustment') && adj.notes) {
        // Extract original adjustment ID from undo operation notes
        // Pattern: "Original ID: 32f334e1-93fa-4d72-866e-450e4df57724"
        const match = adj.notes.match(/Original ID:\s*([a-f0-9-]+)/i);
        if (match && match[1]) {
          undoneIds.add(match[1]);
        }
      }
    });
    
    return undoneIds;
  };

  // Helper function to check if an adjustment is an undo operation
  const isUndoOperation = (adj: any): boolean => {
    return adj.reason && (
      adj.reason.toLowerCase().includes('undo operation') ||
      adj.reason.toLowerCase().includes('undo adjustment') ||
      adj.reason.toLowerCase() === 'undo adjustment'
    );
  };

  // Helper function to calculate correct cost impact based on actual quantity change
  const calculateCorrectCostImpact = (adjustment: any): number => {
    const actualChange = Number(adjustment.actualQuantityChange) || 0;
    const unitCost = Number(adjustment.unitCost) || 0;
    
    // Cost impact should be: actualQuantityChange * unitCost
    // Positive for increases (adding inventory value)
    // Negative for decreases (reducing inventory value)
    return actualChange * unitCost;
  };

  const formatCostImpact = (adjustment: any): string => {
    const correctCostImpact = calculateCorrectCostImpact(adjustment);
    return correctCostImpact.toFixed(2);
  };

  const applyAdjustmentFilters = () => {
    let filtered = [...adjustments];
    
    // Apply date filters first
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(adj => new Date(adj.adjustmentDate) >= start);
    }
    
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(adj => new Date(adj.adjustmentDate) <= end);
    }
    
    // Apply tab-based filtering
    const undoneIds = getUndoneAdjustmentIds();
    
    switch (activeTab) {
      case 'active':
        // Show only adjustments that haven't been undone and aren't undo operations themselves
        filtered = filtered.filter(adj =>
          !undoneIds.has(adj.id) &&
          !isUndoOperation(adj)
        );
        break;
      case 'undone':
        // Show undone adjustments and undo operations
        filtered = filtered.filter(adj =>
          undoneIds.has(adj.id) ||
          isUndoOperation(adj)
        );
        break;
      case 'all':
        // Show all adjustments (no additional filtering)
        break;
    }
    
    setFilteredAdjustments(filtered);
  };

  const exportAdjustmentsToCsv = () => {
    const dataToExport = filteredAdjustments;
    const dateRangeText = startDate && endDate
      ? `_${startDate}_to_${endDate}`
      : startDate
      ? `_from_${startDate}`
      : endDate
      ? `_to_${endDate}`
      : '';
    
    const headers = [
      'Date',
      'Material',
      'Adjustment Type',
      'Quantity',
      'Previous Stock',
      'New Stock',
      'Actual Change',
      'Unit Cost',
      'Total Cost Impact',
      'Reason',
      'Notes',
      'Write-Off',
      'Write-Off Category',
      'Adjusted By',
    ];
    
    const rows = dataToExport.map(adj => [
      new Date(adj.adjustmentDate).toLocaleDateString(),
      adj.material?.name || 'Unknown',
      adj.adjustmentType,
      adj.quantity,
      adj.previousStock || 'N/A',
      adj.newStock || 'N/A',
      adj.actualQuantityChange || 'N/A',
      formatCostImpact(adj.unitCost || 0),
      calculateCorrectCostImpact(adj).toFixed(2),
      adj.reason,
      adj.notes || '',
      adj.isWriteOff ? 'Yes' : 'No',
      adj.writeOffCategory || '',
      adj.adjustedBy,
    ]);

    // Add summary row with improved business metrics
    const netCostImpact = dataToExport.reduce((sum, adj) => sum + calculateCorrectCostImpact(adj), 0);
    const totalActivity = dataToExport.reduce((sum, adj) => sum + Math.abs(calculateCorrectCostImpact(adj)), 0);
    const writeOffTotal = dataToExport
      .filter(adj => adj.isWriteOff)
      .reduce((sum, adj) => sum + Math.abs(calculateCorrectCostImpact(adj)), 0);

    rows.push([]);
    rows.push(['SUMMARY', '', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Total Adjustments', dataToExport.length.toString(), '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Net Cost Impact', '', '', '', '', '', '', '', netCostImpact.toFixed(2), '', '', '', '', '']);
    rows.push(['Total Activity (Absolute)', '', '', '', '', '', '', '', totalActivity.toFixed(2), '', '', '', '', '']);
    rows.push(['Write-Offs (Losses)', '', '', '', '', '', '', '', writeOffTotal.toFixed(2), '', '', '', '', '']);

    let csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");

    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `stock_adjustments_report${dateRangeText}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const undoneIds = getUndoneAdjustmentIds();
  
  // Calculate counts for tab badges
  const activeCount = adjustments.filter(adj =>
    !undoneIds.has(adj.id) &&
    !isUndoOperation(adj)
  ).length;
  
  const undoneCount = adjustments.filter(adj =>
    undoneIds.has(adj.id) ||
    isUndoOperation(adj)
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Stock Adjustments Report
            {currentTemplate && (
              <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                {currentTemplate.name}
              </span>
            )}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {currentTemplate
              ? `Detailed history of inventory adjustments for your ${currentTemplate.name.toLowerCase()} business`
              : 'Detailed history of all inventory adjustments'
            }
          </p>
          {filteredAdjustments.length !== adjustments.length && (
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
              Showing {filteredAdjustments.length} of {adjustments.length} adjustments
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdjustmentFilters(!showAdjustmentFilters)}
            leftIcon={<Filter className="w-4 h-4" />}
          >
            {showAdjustmentFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            size="sm"
            onClick={exportAdjustmentsToCsv}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      {showAdjustmentFilters && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="w-full"
              >
                Clear Dates
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'active'
                  ? 'border-jewelry-primary text-jewelry-primary bg-jewelry-primary/5'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Active Adjustments
              {activeCount > 0 && (
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  activeTab === 'active'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {activeCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('undone')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'undone'
                  ? 'border-jewelry-primary text-jewelry-primary bg-jewelry-primary/5'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              Undone & Reversals
              {undoneCount > 0 && (
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  activeTab === 'undone'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {undoneCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'all'
                  ? 'border-jewelry-primary text-jewelry-primary bg-jewelry-primary/5'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              All Adjustments
              {adjustments.length > 0 && (
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  activeTab === 'all'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {adjustments.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Description */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {activeTab === 'active' && `Shows only adjustments that are currently in effect and impact your ${currentTemplate?.name || 'business'} inventory totals.`}
            {activeTab === 'undone' && 'Shows adjustments that have been reversed and their corresponding undo operations for audit purposes.'}
            {activeTab === 'all' && `Shows complete adjustment history for your ${currentTemplate?.name || 'business'} including active, undone, and undo operations.`}
          </p>
        </div>

        {/* Adjustments Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Material</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Change</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Reason</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cost Impact</th>
                <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Write-Off</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {adjustmentsLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      Loading adjustments...
                    </div>
                  </td>
                </tr>
              ) : filteredAdjustments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {adjustments.length === 0 ? 'No stock adjustments found.' : 'No adjustments match the selected filters.'}
                  </td>
                </tr>
              ) : (
                filteredAdjustments.map(adj => {
                  const isUndone = undoneIds.has(adj.id);
                  const isUndo = isUndoOperation(adj);
                  
                  return (
                    <tr
                      key={adj.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        (isUndone || isUndo) ? 'opacity-60' : ''
                      }`}
                    >
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                        {new Date(adj.adjustmentDate).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                        {adj.material?.name || 'Unknown'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          adj.adjustmentType === 'increase'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : adj.adjustmentType === 'decrease'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {adj.adjustmentType}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-right text-gray-900 dark:text-white">
                        {adj.quantity}
                      </td>
                      <td className="py-3 px-4 text-sm text-right">
                        <span className={`font-medium ${
                          (adj.actualQuantityChange ?? 0) > 0
                            ? 'text-green-600 dark:text-green-400'
                            : (adj.actualQuantityChange ?? 0) < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {(adj.actualQuantityChange ?? 0) > 0 ? '+' : ''}{adj.actualQuantityChange ?? 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                        {adj.reason}
                      </td>
                      <td className={`py-3 px-4 text-sm text-right font-medium ${
                        calculateCorrectCostImpact(adj) >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        ${calculateCorrectCostImpact(adj).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {adj.isWriteOff ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                            Yes
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">No</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      {filteredAdjustments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Adjustments</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{filteredAdjustments.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Net Cost Impact</div>
            <div className={`text-2xl font-bold ${
              filteredAdjustments.reduce((sum, adj) => sum + calculateCorrectCostImpact(adj), 0) >= 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}>
              ${filteredAdjustments.reduce((sum, adj) => sum + calculateCorrectCostImpact(adj), 0).toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {filteredAdjustments.reduce((sum, adj) => sum + calculateCorrectCostImpact(adj), 0) >= 0 ? 'Inventory value increased' : 'Inventory value decreased'}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Activity</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              ${filteredAdjustments.reduce((sum, adj) => sum + Math.abs(calculateCorrectCostImpact(adj)), 0).toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Total dollar amount adjusted
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Write-Offs</div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              ${filteredAdjustments.filter(adj => adj.isWriteOff).reduce((sum, adj) => sum + Math.abs(calculateCorrectCostImpact(adj)), 0).toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Losses for tax/expense purposes
            </div>
          </div>
        </div>
      )}
    </div>
  );
}